import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getPayments, createPayment, updatePayment, deletePayment, getUsers } from '../services/api';
import { Payment, User } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    user: '',
    amount: '',
  });

  useEffect(() => {
    fetchPayments();
    fetchUsers();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await getPayments();
      // Handle the response structure properly - payments are in response.data.data
      const paymentsData = response.data?.data || response.data || [];
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers(1, 1000); // Get all users
      // Handle the response structure properly
      const usersData = response.data?.data || response.data || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    }
  };

  const handleAddPayment = () => {
    setEditingPayment(null);
    setFormData({ user: '', amount: '' });
    setOpenDialog(true);
  };

  const handleEditPayment = (payment: Payment) => {
    setEditingPayment(payment);
    setFormData({
      user: payment.user,
      amount: payment.amount.toString(),
    });
    setOpenDialog(true);
  };

  const handleDeletePayment = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await deletePayment(id);
        setSuccess('Payment deleted successfully');
        fetchPayments();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete payment');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.user || !formData.amount) {
        setError('Please fill in all fields');
        return;
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        setError('Amount must be a positive number');
        return;
      }

      if (editingPayment && editingPayment.id) {
        await updatePayment(editingPayment.id, {
          user: formData.user,
          amount,
        });
        setSuccess('Payment updated successfully');
      } else {
        await createPayment({
          user: formData.user,
          amount,
        });
        setSuccess('Payment created successfully');
      }

      setOpenDialog(false);
      fetchPayments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save payment');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPayment(null);
    setFormData({ user: '', amount: '' });
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'user',
      headerName: 'User',
      width: 200,
      renderCell: (params: any) => {
        if (!params || !params.row) return '';
        if (params.row.fname && params.row.lname) {
          return `${params.row.lname}, ${params.row.fname}`;
        }
        return params.row.user || '';
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      valueFormatter: (value: any) => {
        if (value == null || value === undefined) return '€0.00';
        return `€${Number(value).toFixed(2)}`;
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      valueFormatter: (params: any) => {
        if (params.value == null || params.value === undefined) return '';
        try {
          return new Date(params.value).toLocaleString();
        } catch (error) {
          return '';
        }
      },
    },
    {
      field: 'updated_at',
      headerName: 'Updated',
      width: 180,
      valueFormatter: (params: any) => {
        if (params.value == null || params.value === undefined) return '';
        try {
          return new Date(params.value).toLocaleString();
        } catch (error) {
          return '';
        }
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: any) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEditPayment(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeletePayment(params.row.id)}
        />,
      ],
    },
  ];

  if (!user?.is_admin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied. Admin privileges required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Payments</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPayment}
        >
          Add Payment
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={payments}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPayment ? 'Edit Payment' : 'Add Payment'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>User</InputLabel>
            <Select
              value={formData.user}
              label="User"
              onChange={(e) => setFormData({ ...formData, user: e.target.value })}
            >
              {Array.isArray(users) && users.length > 0 ? (
                users.map((user) => (
                  <MenuItem key={user.email} value={user.email}>
                    {user.lname}, {user.fname}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Loading users...</MenuItem>
              )}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPayment ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payments;
